/** The datasets a member can follow (Feed); contacts are not followable. */
export const WATCH_DATASETS = ['project', 'account'] as const;
export type WatchDataset = (typeof WATCH_DATASETS)[number];

/** Each followable dataset's system Following View: its kind and its fixed name. */
export const SYSTEM_VIEWS = {
  project: { kind: 'following_projects', name: 'Following · Projects' },
  account: { kind: 'following_accounts', name: 'Following · Accounts' },
} as const satisfies Record<WatchDataset, { kind: string; name: string }>;
export type SystemViewKind = (typeof SYSTEM_VIEWS)[WatchDataset]['kind'];
export const SYSTEM_VIEW_KINDS = [
  SYSTEM_VIEWS.project.kind,
  SYSTEM_VIEWS.account.kind,
] as const satisfies readonly SystemViewKind[];

/** One key per followed record, "dataset:record_id", wherever seen state is matched. */
export function feedRecordKey(record: { dataset: string; record_id: string }): string {
  return `${record.dataset}:${record.record_id}`;
}

/** An email mentions a signal only when Parcel found it within this many days of its filing. */
export const FEED_PUSH_FRESHNESS_DAYS = 14;
/** A member's Feed email: off, or a daily digest (the default). */
export const FEED_EMAIL_PREFERENCES = ['off', 'daily'] as const;
export type FeedEmailPreference = (typeof FEED_EMAIL_PREFERENCES)[number];

/** Items count when discovered within this many days. */
export const FEED_WINDOW_DAYS = 30;
/** A followed record shows news from this many days before the follow onward; older history never shows. */
export const FEED_FOLLOW_LOOKBACK_DAYS = 14;
/** A row gets a "What's the latest" summary only while its newest news is at most this many days old. */
export const FEED_LATEST_DAYS = 14;
/** Whether a Feed load writes this row a summary: none yet, a headline signal to key it on, and recent news. */
export function feedSummaryDue(
  row: { why_now: string | null; headline: unknown; last_activity_at: string },
  now: number,
): boolean {
  return (
    row.why_now === null &&
    row.headline !== null &&
    Date.parse(row.last_activity_at) >= now - FEED_LATEST_DAYS * 86_400_000
  );
}
/** Rows per page, and the most a caller may ask for. */
export const FEED_PAGE_MAX = 50;
/** Rows a model reads when it sends no limit, so one call stays small in context. */
export const FEED_TOOL_LIMIT = 10;
/** Items per row a model reads when it sends none; `item_count` still counts them all. */
export const FEED_TOOL_ITEMS = 3;
/** Items carried on one row; `item_count` reports the rest. */
export const FEED_ROW_ITEM_CAP = 20;

/** Priorities in rank order (rank = index + 1); every rank mapping, in SQL or TS, derives from this. */
export const FEED_PRIORITIES = ['low', 'medium', 'high'] as const;
export type FeedPriority = (typeof FEED_PRIORITIES)[number];

export interface FeedVia {
  view_id: string;
  name: string;
  system_kind: SystemViewKind | null;
}

/** One document a signal was read from, as the signal detail shows it. */
export interface FeedSignalSource {
  source_display_name: string | null;
  source_url: string | null;
  source_logo_domain: string | null;
  source_document_id: string | null;
  document_type: string | null;
  document_date: string | null;
}

export interface FeedSignalItem {
  kind: 'signal';
  id: string;
  project_id: string;
  project_name: string | null;
  signal_type: string;
  summary: string | null;
  filed_at: string;
  discovered_at: string;
  priority: FeedPriority;
  sources: FeedSignalSource[];
}

export interface FeedDocumentItem {
  kind: 'document';
  id: string;
  project_id: string;
  project_name: string | null;
  document_type: string | null;
  label: string | null;
  source_url: string | null;
  discovered_at: string;
}

export type FeedItem = FeedSignalItem | FeedDocumentItem;

export interface FeedRow {
  dataset: WatchDataset;
  record_id: string;
  name: string;
  /** A project's "City, ST"; an account's "Type · City". */
  subtitle: string | null;
  /** An account's type label ("Developer"); null for a project. */
  account_type: string | null;
  /** The newest signal, the row's one-liner. */
  headline: FeedSignalItem | null;
  /** The row's model-written why-now, while it was written for its current headline; null until then. */
  why_now: string | null;
  /** The highest subscription priority among the row's signals. */
  priority: FeedPriority | null;
  /** A project's stage; an account's active project count. */
  status: string | null;
  last_activity_at: string;
  /** When Parcel last added an item to the row; pass it as `as_of` to mark the row seen up to what was shown. */
  changed_at: string;
  /** When the record was first followed: added to a watched View, or the View watched, whichever came later. */
  followed_at: string;
  following_via: FeedVia[];
  /** For a project row: the followed accounts it is also linked to. */
  via_accounts: { account_id: string; name: string | null }[];
  /** Null when the caller is not a member (an API key). */
  unseen: boolean | null;
  item_count: number;
  items: FeedItem[];
  image_url: string | null;
  website: string | null;
  /** The record's own score: `icp` on an account row, `atlas` on a project row; null inside when unscored. */
  scores: Partial<Record<'icp' | 'atlas', FeedScoreValue | null>>;
  /** A project's newest filing source (its logo and portal), as the project drawer shows it; null on accounts. */
  latest_source: { display_name: string; logo_domain: string | null; base_url: string } | null;
}

export interface FeedScoreValue {
  score: number;
  bucket: string;
  matched: string[];
}

export interface FeedStats {
  followed_projects: number;
  followed_accounts: number;
  rows_with_news: number;
  unseen_rows: number | null;
}

/** /feed/stats: the counts plus the server's read moment. */
export interface FeedStatsRead extends FeedStats {
  as_of: string;
}

export interface FeedPage {
  rows: FeedRow[];
  next_cursor: string | null;
  /** On the first page only; null on a page read with a cursor. */
  stats: FeedStats | null;
  /** The moment the Feed was read (a later page keeps the first page's); seen-all's as_of. */
  as_of: string;
}

/** A skipped suggestion stays out of Suggested this long, unless its bucket rises first. */
export const SUGGESTION_SKIP_DAYS = 60;
export const SUGGESTION_PAGE_MAX = 25;

/** An unfollowed project or account in its score's top two buckets. */
export interface FeedSuggestion {
  dataset: WatchDataset;
  record_id: string;
  name: string;
  subtitle: string | null;
  score: number;
  bucket: string;
  /** The bucket's position, 0 at the top; Suggested holds 0 and 1. */
  bucket_index: number;
  bucket_label: string;
  /** The domain logo.dev draws: a project's latest source, an account's own site. Absent from an older API. */
  logo_domain?: string | null;
  /** The matched criteria's labels, in definition order. */
  why: string[];
  last_activity_at: string | null;
  website: string | null;
}
